import { Box, TextField } from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";

import { TableActions } from "../../components/v3";
import {
  VendorStatementTypes,
  VendorStatementFilter,
  getStatementTypeLabel,
  STATEMENT_PURCHASE_COLORS,
} from "../../components/vendor/types";
import { getBalanceColor } from "../../utils";

/**
 * Route paths for the vendor module.
 * Single source of truth — reuse these instead of hard-coding `/vendors...` or `/vendor...` strings.
 */
export const VENDOR_ROUTES = {
  root: "/vendors",
  details: "/vendor/:id",
  addPurchase: "/vendor/purchase/add",
  editPurchase: "/vendor/purchase/edit/:id",
  invoice: "/vendor/:vendorId/invoice/:invoiceId",
  invoiceReturn: "/vendor/:vendorId/invoice/:invoiceId/return",
  return: "/vendor/:vendorId/return/:returnId",
  payment: "/vendor/:vendorId/payment/:paymentId",
} as const;

export const VendorTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.4, sortable: false },
  {
    field: "name",
    headerName: "Name",
    flex: 2,
    sortable: true,
  },
  {
    field: "locality",
    headerName: "Place",
    flex: 2,
    sortable: false,
  },
  {
    field: "primaryPhone",
    headerName: "Phone Number",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "formattedBalance",
    headerName: "Balance",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const value = params.value;
      const rawBalance = params.row.balance;
      return <Box sx={{ color: getBalanceColor(parseFloat(rawBalance)), display: "flex", alignItems: "center" }}>{value}</Box>;
    },
  },
];

const buildVendorActionsColumn = (
  handleActions,
  hasEditPurchase: boolean,
  hasDeletePurchase: boolean,
  hasDeletePurchaseReturn: boolean,
  hasEditPayment: boolean,
  hasDeletePayment: boolean,
) => ({
  field: "actions",
  headerName: "Action",
  flex: 1,
  sortable: false,
  renderCell: (params) => {
    const { type, meta } = params.row;
    const actions = [];

    if (type === VendorStatementTypes.purchase_invoice && !meta?.isOldBalance) {
      if (hasEditPurchase) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }

      if (hasDeletePurchase) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
    } else if (type === VendorStatementTypes.purchase_return) {
      if (hasDeletePurchaseReturn) {
        actions.push({
          icon: Delete,
          type: "cancelReturn",
          label: "Cancel Return",
          onClick: handleActions,
        });
      }
    } else if (type === VendorStatementTypes.transaction) {
      if (hasEditPayment) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }

      if (hasDeletePayment) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
    }
    return <TableActions item={params} actions={actions} />;
  },
});

export const getStatementTableColumns = (
  handleActions,
  hasEditPurchase: boolean,
  hasDeletePurchase: boolean,
  hasDeletePurchaseReturn: boolean,
  hasEditPayment: boolean,
  hasDeletePayment: boolean,
  selectedFilter,
) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.75, sortable: false },
    { field: "date", headerName: "Date", flex: 1, sortable: true, sortingOrder: ["desc", "asc", null] },
    { field: "projectOrAccount", headerName: "Project / Account", flex: 1.5, sortable: false },
    { field: "description", headerName: "description", flex: 1.5, sortable: false },
    {
      field: "typeName",
      headerName: "Type",
      flex: 1,
      sortable: false,
      renderCell: (params) => getStatementTypeLabel(params.row.type),
    },
    {
      field: "purchase",
      headerName: "Purchase",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        const color = STATEMENT_PURCHASE_COLORS[params.row.type] || "red";
        return <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
      },
    },
    // TBD
    // { field: "gst", headerName: "GST", flex: 1 },
    {
      field: "payment",
      headerName: "Payment",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>
        );
      },
    },
    // TBD
    // { field: "balance", headerName: "Balance", flex: 1 },
  ];

  if (hasEditPurchase || hasDeletePurchase || hasDeletePurchaseReturn || hasEditPayment || hasDeletePayment) {
    headers.push(
      buildVendorActionsColumn(
        handleActions,
        hasEditPurchase,
        hasDeletePurchase,
        hasDeletePurchaseReturn,
        hasEditPayment,
        hasDeletePayment,
      ),
    );
  }

  return selectedFilter === VendorStatementFilter.purchase
    ? headers.filter((col) => col.field !== "payment")
    : selectedFilter === VendorStatementFilter.transaction
      ? headers.filter((col) => col.field !== "purchase" && col.field !== "typeName")
      : headers;
};

export const getLedgerTableColumns = (
  handleActions,
  hasEditPurchase: boolean,
  hasDeletePurchase: boolean,
  hasDeletePurchaseReturn: boolean,
  hasEditPayment: boolean,
  hasDeletePayment: boolean,
) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.75, sortable: false },
    { field: "date", headerName: "Date", flex: 1, sortable: false },
    { field: "projectOrAccount", headerName: "Project / Account", flex: 1.5, sortable: false },
    { field: "description", headerName: "description", flex: 1.5, sortable: false },
    {
      field: "typeName",
      headerName: "Type",
      flex: 1,
      sortable: false,
      renderCell: (params) => getStatementTypeLabel(params.row.type),
    },
    {
      field: "purchase",
      headerName: "Purchase",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        const color = STATEMENT_PURCHASE_COLORS[params.row.type] || "red";
        return <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>;
      },
    },
    {
      field: "payment",
      headerName: "Payment",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        if (!value) return "-";
        return (
          <span style={{ color: "green", display: "flex", alignItems: "center", gap: 4 }}>₹ {Number(value).toFixed(2)}</span>
        );
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const rawBalance = params.value;
        if (rawBalance === null || rawBalance === undefined || rawBalance === "") return "-";
        // Invert the stored balance for display, to match formatBalance and the rest of the app.
        const num = -(Number(rawBalance) || 0);
        const formatted = `${num < 0 ? "-" : ""}₹ ${Math.abs(num).toFixed(2)}`;
        return <Box sx={{ color: getBalanceColor(num), display: "flex", alignItems: "center" }}>{formatted}</Box>;
      },
    },
  ];

  if (hasEditPurchase || hasDeletePurchase || hasDeletePurchaseReturn || hasEditPayment || hasDeletePayment) {
    headers.push(
      buildVendorActionsColumn(
        handleActions,
        hasEditPurchase,
        hasDeletePurchase,
        hasDeletePurchaseReturn,
        hasEditPayment,
        hasDeletePayment,
      ),
    );
  }

  return headers;
};

export const PurchaseItemColumns = () => [
  {
    field: "name",
    headerName: "Item Name",
    flex: 2,
    editable: false,
    renderCell: (params) => params.row.name || "-",
  },

  {
    field: "quantity",
    headerName: "Quantity",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: true,
    valueSetter: (value, row) => ({ ...row, quantity: value }),
    renderCell: (params) => {
      const qty = params.value;
      const unit = params.row.unitOfMeasure;
      if (!qty) return "-";
      return unit ? `${qty} ${unit}` : qty;
    },
  },

  {
    field: "price",
    headerName: "Price",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: true,
    valueSetter: (value, row) => ({ ...row, price: value }),
    renderCell: (params) => (params.value ? `₹ ${params.value}` : "-"),
  },
  {
    field: "taxPercentage",
    headerName: "Tax %",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: true,
    valueSetter: (value, row) => ({ ...row, taxPercentage: value }),
    renderCell: (params) => (params.value ? `${params.value}%` : "-"),
  },
  {
    field: "total",
    headerName: "Total",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: false,
    type: "number",
    valueGetter: (value, row) => {
      const qty = Number(row.quantity) || 0;
      const price = Number(row.price) || 0;
      const tax = Number(row.taxPercentage) || 0;
      const subtotal = qty * price;
      return subtotal * (1 + tax / 100);
    },
    renderCell: (params) => {
      const total = params.value;
      return total ? `₹ ${total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "-";
    },
  },
];

export const PurchaseReturnColumns = (handleActions) => [
  { field: "items", headerName: "Item", flex: 1 },
  { field: "price", headerName: "Price", flex: 1 },
  { field: "quantity", headerName: "Quantity", flex: 1 },
  { field: "total", headerName: "Total", flex: 1 },
  {
    field: "action",
    headerName: "Action",
    flex: 1,
    renderCell: (params) => (
      <TableActions
        item={params}
        actions={[
          { icon: Edit, type: "edit", label: "Edit", onClick: handleActions },
          {
            icon: Delete,
            type: "delete",
            label: "Delete",
            onClick: handleActions,
          },
        ]}
      />
    ),
  },
];

export const PurchaseReturnItemColumns = () => [
  {
    field: "name",
    headerName: "Item Name",
    flex: 2,
    editable: false,
    renderCell: (params) => params.row.name || "-",
  },

  {
    field: "originalQuantity",
    headerName: "Purchased Qty",
    flex: 1,
    editable: false,
  },

  {
    field: "returnQuantity",
    headerName: "Return Qty",
    flex: 1,
    editable: true,
    type: "number",

    // 🔹 Visual validation
    preProcessEditCellProps: (params) => {
      const value = Number(params.props.value);
      const max = Number(params.row.originalQuantity);

      const error = isNaN(value) || value < 0 || value > max;

      return {
        ...params.props,
        error,
      };
    },

    // 🔹 Correct valueSetter signature
    valueSetter: (value, row) => {
      const numericValue = Number(value);
      const max = Number(row.originalQuantity);

      if (isNaN(numericValue)) return row;
      if (numericValue < 0) return row;
      if (numericValue > max) return row;

      return {
        ...row,
        returnQuantity: numericValue,
      };
    },

    renderEditCell: (params) => {
      const value = Number(params.value);
      const max = Number(params.row.originalQuantity);

      let error = false;
      let helperText = "";

      if (isNaN(value)) {
        error = true;
        helperText = "Invalid quantity";
      } else if (value < 0) {
        error = true;
        helperText = "Quantity cannot be negative";
      } else if (value > max) {
        error = true;
        helperText = `Cannot exceed ${max}`;
      }

      return (
        <TextField
          type="number"
          value={params.value ?? ""}
          error={error}
          helperText={helperText}
          size="small"
          fullWidth
          onChange={(e) =>
            params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value,
            })
          }
        />
      );
    },
  },

  {
    field: "price",
    headerName: "Price",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: false,
    valueSetter: (value, row) => ({ ...row, price: value }),
    renderCell: (params) => (params.value ? `₹ ${params.value}` : "-"),
  },
  {
    field: "taxPercentage",
    headerName: "Tax %",
    type: "number",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: false,
    valueSetter: (value, row) => ({ ...row, taxPercentage: value }),
    renderCell: (params) => (params.value ? `${params.value}%` : "-"),
  },
  {
    field: "total",
    headerName: "Total",
    align: "left",
    headerAlign: "left",
    flex: 1,
    editable: false,
    type: "number",
    valueGetter: (value, row) => {
      const qty = Number(row.returnQuantity) || 0;
      const price = Number(row.price) || 0;
      const tax = Number(row.taxPercentage) || 0;

      const subtotal = qty * price;
      return subtotal * (1 + tax / 100);
    },
    renderCell: (params) => {
      const total = params.value;
      return total ? `₹ ${total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "-";
    },
  },
];
